/** First message pages per chat, kept for the signed-in user across visits to
 *  Messages, so opening a chat draws at once while the chat refreshes itself.
 *  Filled in the background a few chats at a time, never as one burst of edge
 *  calls; sign-out drops it. */

import { MessagingService } from '../../services/MessagingService';
import type { Message } from '../../types/messaging';
import { runWarmTasks } from '../../services/mainScreenWarm';

export interface MessageCacheEntry {
  messages: Message[];
  hasMore: boolean;
  nextCursor: string | null;
}

export const MESSAGES_PAGE_SIZE = 50;
/** A warm fetches a chat again once its page is older than this. */
export const MESSAGE_PAGE_FRESH_MS = 5 * 60_000;

/** Records when each page was written, by MessagesView's own updates as much as by the warm. */
class TimedPages extends Map<string, MessageCacheEntry> {
  readonly writtenAt = new Map<string, number>();
  override set(chatId: string, entry: MessageCacheEntry): this {
    this.writtenAt.set(chatId, Date.now());
    return super.set(chatId, entry);
  }
  override delete(chatId: string): boolean {
    this.writtenAt.delete(chatId);
    return super.delete(chatId);
  }
  fresh(chatId: string): boolean {
    return this.has(chatId) && Date.now() - (this.writtenAt.get(chatId) ?? 0) < MESSAGE_PAGE_FRESH_MS;
  }
}

const pagesByUser = new Map<string, TimedPages>();
const inFlight = new Map<string, Promise<void>>();
let generation = 0;

/** The live page map for one user. Callers read and write it directly. */
export function messagePageCache(userId: string): Map<string, MessageCacheEntry> {
  return pagesFor(userId);
}

function pagesFor(userId: string): TimedPages {
  let pages = pagesByUser.get(userId);
  if (!pages) {
    // One signed-in user at a time; a second account never sees the first's pages.
    pagesByUser.clear();
    pages = new TimedPages();
    pagesByUser.set(userId, pages);
  }
  return pages;
}

/**
 * Fetches the first page of each chat without a fresh one, in list order, a few
 * at a time. `isCurrent` stops further fetches starting; a page already fetched
 * is still kept, and a chat already being fetched is joined, not fetched twice.
 */
export async function warmChatPages(
  userId: string,
  chatIds: readonly string[],
  { isCurrent = () => true, concurrency = 4 }: { isCurrent?: () => boolean; concurrency?: number } = {},
): Promise<void> {
  const started = generation;
  const pages = pagesFor(userId);
  const owned = () => generation === started && pagesByUser.get(userId) === pages;
  const fetchPage = (chatId: string): Promise<void> => {
    const key = JSON.stringify([userId, chatId]);
    const pending = inFlight.get(key);
    if (pending) return pending;
    const requestedAt = Date.now();
    const request = MessagingService.getMessagesPage(chatId, { limit: MESSAGES_PAGE_SIZE }).then((page) => {
      // An opened chat may have written a newer page, with realtime messages, meanwhile.
      if (!owned() || (pages.writtenAt.get(chatId) ?? 0) > requestedAt) return;
      pages.set(chatId, {
        messages: page.messages.map((m) => ({ ...m, isMe: m.sender_id === userId })),
        hasMore: page.hasMore,
        nextCursor: page.nextCursor,
      });
    });
    inFlight.set(key, request);
    void request.finally(() => { if (inFlight.get(key) === request) inFlight.delete(key); }).catch(() => {});
    return request;
  };
  await runWarmTasks(
    chatIds.filter((id) => !pages.fresh(id)).map((chatId) => () => (pages.fresh(chatId) ? Promise.resolve() : fetchPage(chatId))),
    () => owned() && isCurrent(),
    concurrency,
  );
}

export function clearMessagePageCache(): void {
  generation++;
  pagesByUser.clear();
  inFlight.clear();
}

if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', clearMessagePageCache);
